import { CheckCircle } from '@phosphor-icons/react';
import QuoteForm from './QuoteForm';
import QuoteFormAd from './QuoteFormAd';

const BULLETS = [
  { bold: 'World-class fulfillment', text: 'with 99.8% accuracy, 24hr processing, and 1-3 day nationwide shipping — turning happy customers into repeat buyers.' },
  { bold: 'Increase revenue', text: 'with branded packaging, seamless unboxing experiences, and scalable omnichannel capabilities built for growth.' },
  { bold: 'Save money', text: 'with transparent, all-in pricing. No hidden fees, no surprise surcharges. Just honest fulfillment built for your bottom line.' },
];

export default function HeroSection({ formVariant = 'default' }) {
  const FormComponent = formVariant === 'ad' ? QuoteFormAd : QuoteForm;
  return (
    <section id="hero" data-testid="hero-section" className="relative">
      {/* Dark navy bg */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 30% 50%, #132D46 0%, #191E29 70%)' }} />
      {/* Image right — desktop only, with dark tinted overlay */}
      <div className="absolute top-0 right-0 w-[42%] h-full hidden lg:block overflow-hidden">
        <img src="/lp/WarehouseImagery/EXPORT 1.jpg" alt="Ship Velocity warehouse" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#132D46]/70" />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 lg:pl-4 lg:pr-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-center py-10 sm:py-14 lg:py-20 gap-8">

          {/* LEFT — desktop: content, mobile: hidden (shown below form) */}
          <div className="lg:col-span-5 space-y-6 lg:space-y-7 lg:pr-4 hidden lg:block">
            <a href="https://shipvelocity.com" target="_blank" rel="noopener noreferrer"><img src="/lp/NavbarLogo.png" alt="Ship Velocity" className="h-10 lg:h-12 w-auto" /></a>

            <h1 data-testid="hero-headline" className="text-5xl lg:text-[4rem] lg:whitespace-nowrap font-extrabold leading-[1.06] tracking-tight text-white">
              Ship with confidence,<br />
              <span className="text-[#07c28e]">Scale with Velocity</span>
            </h1>

            <div className="space-y-5">
              {BULLETS.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle size={22} weight="fill" className="text-[#07c28e] mt-1 flex-shrink-0 w-[26px] h-[26px]" />
                  <p className="text-[18px] text-slate-300 leading-relaxed">
                    <strong className="text-white font-bold">{b.bold}</strong> {b.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT — Form (wider on desktop) */}
          <div className="lg:col-span-7 flex flex-col items-center lg:items-end relative">
            {/* Mobile: logo + headline ABOVE form */}
            <div className="lg:hidden w-full space-y-4 mb-8">
              <a href="https://shipvelocity.com" target="_blank" rel="noopener noreferrer"><img src="/lp/NavbarLogo.png" alt="Ship Velocity" className="h-10 w-auto" /></a>
              <h1 className="text-[2.25rem] sm:text-5xl font-extrabold leading-[1.06] tracking-tight text-white">
                Ship with confidence,<br />
                <span className="text-[#07c28e]">Scale with Velocity</span>
              </h1>
            </div>

            <FormComponent />

            {/* Mobile: bullets BELOW form */}
            <div className="lg:hidden w-full space-y-4 mt-8">
              {BULLETS.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle size={20} weight="fill" className="text-[#07c28e] mt-1 flex-shrink-0" />
                  <p className="text-[15px] text-slate-300 leading-relaxed">
                    <strong className="text-white font-bold">{b.bold}</strong> {b.text}
                  </p>
                </div>
              ))}
            </div>

            {/* Mobile: testimonials BELOW bullets */}
            <div className="lg:hidden w-full mt-8 space-y-4">
              <p className="text-xs font-bold text-[#07c28e] tracking-[0.15em] uppercase">What Our Clients Say</p>
              {[
                { name: 'Mark Wagner', role: 'Disputifier', quote: "The service at Velocity is bar-none. They're truly invested in long term relationships and will move mountains to ensure that you're happy.", img: '/lp/Testimonial_photos/MarkWagner.png' },
                { name: 'Damon Dixon', role: 'Douxds', quote: "Velocity made it very easy for us to move our fulfillment from China to Miami. They also got me started on Amazon which unlocked another stream of revenue.", img: '/lp/Testimonial_photos/DamonDixon.png' },
                { name: 'Hugo Contreras', role: 'Paraiso Creations', quote: "These guys are super organized and really pay attention to the details. As we added more SKUs and scaled, they made the process feel simple and stress free.", img: '/lp/Testimonial_photos/HugoContrerasJr.png' },
              ].map((r, i) => (
                <div key={i} className="bg-white/5 border border-white/8 rounded-xl p-4">
                  <p className="text-[13px] text-slate-300 leading-relaxed mb-3">"{r.quote}"</p>
                  <div className="flex items-center gap-2.5">
                    <img src={r.img} alt={r.name} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <p className="text-xs font-bold text-white">{r.name}</p>
                      <p className="text-[10px] text-slate-500">{r.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <LogoBar />
    </section>
  );
}

function LogoBar() {
  const logos = [
    { src: '/lp/client_logos/Douxds.png', alt: 'Douxds' },
    { src: '/lp/client_logos/La Petite Creme.png', alt: 'La Petite Creme' },
    { src: '/lp/client_logos/Paraiso.png', alt: 'Paraiso' },
    { src: '/lp/client_logos/Tratez.png', alt: 'Tratez' },
  ];
  const doubled = [...logos, ...logos];

  return (
    <div data-testid="hero-logo-bar" className="relative z-20 bg-[#191E29] py-6 sm:py-8 overflow-hidden border-t border-white/5">
      <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-[#191E29] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-[#191E29] to-transparent z-10 pointer-events-none" />
      <div className="flex animate-marquee items-center">
        {doubled.map((l, i) => (
          <div key={`${l.alt}-${i}`} className="flex-shrink-0 mx-8 sm:mx-12 md:mx-20">
            <img src={l.src} alt={l.alt} className="h-8 sm:h-12 md:h-14 lg:h-16 w-auto object-contain brightness-0 invert opacity-60 hover:opacity-100 transition-opacity duration-300" />
          </div>
        ))}
      </div>
    </div>
  );
}
